import type { DeviceSetting, State as DeviceSettings } from '#~/device/settings/types.js';
import {
  DeviceRegistryCommand,
  DeviceRegistryCommandKey,
  DeviceRegistryCommandFunctionType,
  CommandType
} from '@tryiris-ai/common.protojs/api/v1/device_settings';
import { rangeToRange, buildCommand, getParamValue } from './helpers.js';
import type { T_Maybe } from '@tryiris-ai/js-types';

const BLOCK_INQUIRY_RESPONSE_TEMPLATE = '90 50 0p 0p 0q 0q 0r 0s tt 0u vv ww 00 xx 0z FF'; // p,q,r,s,t,u,v,w,x,z

/**
 * Processes the special block inquiry command.
 */
export function processBlockInquiry(
  settings: DeviceSettings,
  commands: DeviceRegistryCommand[]
): T_Maybe<Uint8Array> {
  // console.log('>> Processing Block Inquiry command');

  const getValue = (inquiryKey: DeviceRegistryCommandKey): number | undefined =>
    getValueFromDeviceSettings(settings, commands, inquiryKey);

  // Map keys to their descriptive names for clarity
  const inquiryValues = {
    redGain: getValue(DeviceRegistryCommandKey.WHITE_BALANCE_RED_GAIN),
    blueGain: getValue(DeviceRegistryCommandKey.WHITE_BALANCE_BLUE_GAIN),
    whiteBalanceMode: getValue(DeviceRegistryCommandKey.WHITE_BALANCE_MODE),
    aperture: 0, // Original code: Unsure for now.
    exposureMode: getValue(DeviceRegistryCommandKey.EXPOSURE_MODE),
    backlightComp: getValue(DeviceRegistryCommandKey.IMAGE_BACKLIGHT_COMPENSATION), // for u-byte bit 2
    expCompEnabled: getValue(DeviceRegistryCommandKey.EXPOSURE_COMPENSATION_ENABLE), // for u-byte bit 1
    shutterSpeed: getValue(DeviceRegistryCommandKey.EXPOSURE_SHUTTER),
    iris: getValue(DeviceRegistryCommandKey.EXPOSURE_IRIS),
    brightness: getValue(DeviceRegistryCommandKey.IMAGE_BRIGHTNESS),
    exposureCompValue: getValue(DeviceRegistryCommandKey.EXPOSURE_COMPENSATION)
  };

  // Check if any essential value is undefined
  const requiredValues = [
    inquiryValues.redGain,
    inquiryValues.blueGain,
    inquiryValues.whiteBalanceMode,
    inquiryValues.exposureMode,
    inquiryValues.backlightComp,
    inquiryValues.expCompEnabled,
    inquiryValues.shutterSpeed,
    inquiryValues.iris,
    inquiryValues.brightness,
    inquiryValues.exposureCompValue
  ];

  if (requiredValues.some((v) => v === undefined)) {
    return;
  }

  // Construct the 'u' byte: bit 1 for exposureCompEnabled, bit 2 for backlightComp
  let uByte = 0;
  if (inquiryValues.expCompEnabled) uByte |= 1 << 1; // 0-indexed bit
  if (inquiryValues.backlightComp) uByte |= 1 << 2; // 0-indexed bit

  const responseParams = [
    inquiryValues.redGain, // p
    inquiryValues.blueGain, // q
    inquiryValues.whiteBalanceMode, // r
    inquiryValues.aperture, // s
    inquiryValues.exposureMode, // t
    uByte, // u (combined byte)
    inquiryValues.shutterSpeed, // v
    inquiryValues.iris, // w
    inquiryValues.brightness, // x
    inquiryValues.exposureCompValue // z
  ];

  return buildCommand(BLOCK_INQUIRY_RESPONSE_TEMPLATE, responseParams as number[]);
}

/**
 * Processes standard (non-block) inquiry commands.
 */
export function processInquiry(
  settings: DeviceSettings,
  commands: DeviceRegistryCommand[],
  inquiryKey: DeviceRegistryCommandKey
): T_Maybe<Uint8Array> {
  const currentSetting = settings[inquiryKey];
  if (!currentSetting) {
    console.error(`>> Standard Inquiry Error: No setting found for key: ${inquiryKey}`);
    return;
  }

  const cmdDef = commands.find((c) => c.key === inquiryKey);
  if (!cmdDef) {
    console.error(`>> Standard Inquiry Error: No command definition found for key: ${inquiryKey}`);
    return;
  }

  let responseBytes: Uint8Array | undefined;

  if (currentSetting.direct !== undefined) {
    responseBytes = getDirectInquiryResponseBytes(cmdDef, currentSetting.direct);
  } else if (currentSetting.select !== undefined) {
    responseBytes = getSelectInquiryResponseBytes(cmdDef, currentSetting.select);
  } else if (currentSetting.toggle !== undefined) {
    responseBytes = getToggleInquiryResponseBytes(cmdDef, currentSetting.toggle);
  } else {
    console.error(
      `>> Standard Inquiry Error: No valid inquiry response found for key: ${inquiryKey}`
    );
    return;
  }
  return responseBytes;
}

function getDirectInquiryResponseBytes(
  cmdDef: DeviceRegistryCommand,
  directValue: number
): Uint8Array | undefined {
  if (!cmdDef.value) {
    console.error(
      `>> Inquiry Response Error (Direct): Command ${cmdDef.key} has 'direct' but no '.value' definition.`
    );
    return undefined;
  }

  const setDefinition = cmdDef.definitions?.find(
    (d) => d.type === DeviceRegistryCommandFunctionType.SET
  );
  const responseTemplate = setDefinition?.visca?.responseTemplate;
  if (!responseTemplate) {
    console.error(
      `>> Inquiry Response Error (Direct): No SET response template for ${cmdDef.key}.`
    );
    return undefined;
  }

  let unNormalizedValue: number;
  const offset = cmdDef.value.offset || 0;

  if (cmdDef.value.rangeValue) {
    unNormalizedValue =
      rangeToRange(
        { min: 0, max: 1 }, // Assuming directValue is normalized 0-1
        { min: cmdDef.value.rangeValue.min, max: cmdDef.value.rangeValue.max },
        directValue
      ) + offset;
  } else if (cmdDef.value.labels && cmdDef.value.labels.length > 0) {
    // Map normalized value (0-1) to an index in the labels array (0 to length-1)
    unNormalizedValue = Math.round(directValue * (cmdDef.value.labels.length - 1)) + offset;
  } else {
    console.error(
      `>> Inquiry Response Error (Direct): Command ${cmdDef.key} .value lacks rangeValue or non-empty labels.`
    );
    return undefined; // Or use cmdDef.value.default if appropriate here
  }
  return buildCommand(responseTemplate, [unNormalizedValue]);
}

function getSelectInquiryResponseBytes(
  cmdDef: DeviceRegistryCommand,
  selectIndex: number
): Uint8Array | undefined {
  const selectedOption = cmdDef.select?.options?.[selectIndex];
  const commandBytes = selectedOption?.visca?.responseBytes;

  if (!commandBytes) {
    console.error(
      `>> Inquiry Response Error (Select): No responseBytes for option index ${selectIndex} in ${cmdDef.key}.`
    );
    return undefined;
  }
  return commandBytes;
}

function getToggleInquiryResponseBytes(
  cmdDef: DeviceRegistryCommand,
  toggleState: boolean
): Uint8Array | undefined {
  const targetType = toggleState
    ? DeviceRegistryCommandFunctionType.ON
    : DeviceRegistryCommandFunctionType.OFF;
  const definition = cmdDef.definitions?.find((d) => d.type === targetType);
  const commandBytes = definition?.visca?.responseBytes;

  if (!commandBytes) {
    console.error(
      `>> Inquiry Response Error (Toggle): No responseBytes for state ${targetType} in ${cmdDef.key}.`
    );
    return undefined;
  }
  return commandBytes;
}

/**
 * Extracts a numeric value from device settings.
 * If the setting exists, it's processed. Otherwise, tries to return a default from the command definition.
 * This is primarily used by the BLOCK_BEC_INQ handler.
 */
function getValueFromDeviceSettings(
  settings: DeviceSettings,
  commands: DeviceRegistryCommand[],
  key: DeviceRegistryCommandKey
): number | undefined {
  const setting = settings[key];
  const cmdDef = commands.find((c) => c.key === key);

  // console.log(`>> getValue - Key: ${key}, Setting Found: ${!!setting}, Command Found: ${!!cmdDef}`);

  if (!cmdDef) {
    console.error(`>> getValue Error: No command definition found for key: ${key}`);
    return undefined;
  }

  if (setting) {
    return extractValue(cmdDef, setting);
  }

  // If setting is not found, attempt to provide a default value from the command definition
  // console.log(`>> getValue: No setting for ${key}, attempting command default.`);
  switch (cmdDef.type) {
    case CommandType.CMD_VALUE:
      return cmdDef.value?.default;
    case CommandType.CMD_SELECT:
      const defaultOption = cmdDef.select?.options?.find((o: any) => o.default);
      const defaultBytes = defaultOption?.visca?.responseBytes;
      if (!defaultBytes) {
        // console.warn(`>> getValue (Default): No default option with responseBytes for CMD_SELECT (${key}).`);
        return undefined;
      }
      return parseViscaParamValue(defaultBytes);
    case CommandType.CMD_TOGGLE:
      // Default to 0 (OFF).
      return 0;
    default:
      // console.warn(`>> getValue (Default): Unknown command type ${cmdDef.type} for key ${key}.`);
      return undefined;
  }
}

/**
 * Extracts a numeric value from a concrete (existing) setting object based on its type.
 */
function extractValue(cmdDef: DeviceRegistryCommand, setting: DeviceSetting): number | undefined {
  if (setting.direct !== undefined) {
    if (!cmdDef.value) {
      console.error(
        `>> extractValue Error (Direct): Command ${cmdDef.key} is 'direct' but no '.value' definition.`
      );
      return;
    }
    const offset = cmdDef.value.offset || 0;
    if (cmdDef.value.rangeValue) {
      return (
        rangeToRange(
          { min: 0, max: 1 }, // Assuming 'direct' is always normalized 0-1
          { min: cmdDef.value.rangeValue.min, max: cmdDef.value.rangeValue.max },
          setting.direct
        ) + offset
      );
    } else if (cmdDef.value.labels && cmdDef.value.labels.length > 0) {
      // Map normalized value (0-1) to an index in the labels array (0 to length-1)
      return Math.round(setting.direct * (cmdDef.value.labels.length - 1)) + offset;
    }
    return cmdDef.value.default; // Fallback
  }

  if (setting.select !== undefined) {
    const selectedOption = cmdDef.select?.options?.[setting.select];
    const responseBytes = selectedOption?.visca?.responseBytes;
    // The response template seems to be empty.
    // Get the int value of the visca response bytes.
    // Assuming the responses are only two types:
    // "90 50 0v FF" or "90 50 00 00 0v 0v FF"
    // TODO: Make sure this is the case.
    if (responseBytes) {
      return parseViscaParamValue(responseBytes);
    }

    // Fallback to default option if current selected option has no response bytes
    // console.warn(`>> extractValue (Select): No responseBytes for selected option ${setting.select} in ${cmdDef.key}. Trying default.`);
    const defaultOption = cmdDef.select?.options?.find((o: any) => o.default);
    const defaultBytes = defaultOption?.visca?.responseBytes;
    if (defaultBytes) return parseViscaParamValue(defaultBytes);

    console.error(
      `>> extractValue Error (Select): No responseBytes for selected or default option for ${cmdDef.key}.`
    );
    return;
  }

  if (setting.toggle !== undefined) {
    return setting.toggle ? 1 : 0;
  }

  console.error(
    `>> extractValue Error: Setting for ${cmdDef.key} has no direct, select, or toggle value.`,
    setting
  );
  return;
}

/**
 * Parses specific VISCA response byte patterns to extract a numeric value.
 */
function parseViscaParamValue(responseBytes: Uint8Array): number | undefined {
  const TEMPLATE_4_BYTE_RESPONSE = '90 50 0v FF';
  const TEMPLATE_7_BYTE_RESPONSE = '90 50 00 00 0v 0v FF'; // Assumes '0v 0v' is a single value placeholder

  let valueArray: number[] | undefined;

  if (responseBytes.length === 4) {
    valueArray = getParamValue(TEMPLATE_4_BYTE_RESPONSE, responseBytes);
  } else if (responseBytes.length === 7) {
    valueArray = getParamValue(TEMPLATE_7_BYTE_RESPONSE, responseBytes);
  } else {
    return undefined;
  }

  if (!valueArray || valueArray.length !== 1) {
    console.error('>> Value Parsing: Could not extract a single parameter value from response.', {
      responseBytes,
      valueArray
    });
    return undefined;
  }
  return valueArray[0];
}
